/**
 * Heuristic token estimation.
 *
 * Primary rule of thumb from the brief: ~4 characters per token. We blend that
 * with a word/punctuation heuristic so short, punctuation-heavy prompts (JSON
 * schemas, regexes, code) don't get wildly under-counted.
 *
 * Everything is deterministic — no network, no wasm, instant on every keystroke.
 */

export interface TokenStats {
  chars: number;
  words: number;
  punctuation: number;
  lines: number;
  tokens: number;
}

const WORD_RE = /[A-Za-z0-9À-ÿ_]+/g;
const PUNCT_RE = /[^\sA-Za-z0-9À-ÿ_]/g;

/** Full breakdown used by the estimator panel. */
export function tokenStats(text: string): TokenStats {
  if (!text) {
    return { chars: 0, words: 0, punctuation: 0, lines: 0, tokens: 0 };
  }
  const chars = text.length;
  const words = text.match(WORD_RE)?.length ?? 0;
  const punctuation = text.match(PUNCT_RE)?.length ?? 0;
  const lines = text.split('\n').length;

  const charBased = chars / 4;
  const wordBased = words + punctuation * 0.75 + Math.max(0, lines - 1) * 0.5;
  const tokens = Math.max(0, Math.ceil((charBased + wordBased) / 2));

  return { chars, words, punctuation, lines, tokens };
}

/** Fast path — just the estimate. */
export function estimateTokens(text: string): number {
  return tokenStats(text).tokens;
}

/**
 * Blend a prompt estimate with an assumed completion size, mirroring how
 * chat APIs bill (input tokens + output tokens on every call).
 */
export function estimateCallTokens(inputText: string, outputTokens: number): {
  input: number;
  output: number;
  total: number;
} {
  const input = estimateTokens(inputText);
  const output = Math.max(0, Math.round(outputTokens));
  return { input, output, total: input + output };
}
